import React, { useState, useEffect, useRef, useCallback } from 'react';
import { restaurantAPI, deliveryAPI } from '../services/api';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
/** Resolve image URLs — prepend backend origin for relative /static/ paths */
const resolveImg = (url) => {
  if (!url) return null;
  if (url.startsWith('http://') || url.startsWith('https://')) return url;
  return `${BACKEND_URL}${url}`;
};

// ─── Business type icons & labels ────────────────────────────────────────────
const BUSINESS_TYPES = [
  { key: '', label: 'All', icon: '🍽' },
  { key: 'restaurant', label: 'Restaurants', icon: '🍴' },
  { key: 'grocery', label: 'Groceries', icon: '🛒' },
  { key: 'cafe', label: 'Cafes', icon: '☕' },
];

const ALLERGEN_ICONS = {
  gluten: '🌾', nuts: '🥜', dairy: '🥛', eggs: '🥚',
  fish: '🐟', shellfish: '🦐', soya: '🫘', celery: '🥬',
  mustard: '🌿', sesame: '🌱', sulphites: '🍷', lupin: '🌸',
  molluscs: '🦑', peanuts: '🥜', crustaceans: '🦞'
};

const SPICE = ['', '🌶', '🌶🌶', '🌶🌶🌶'];

export default function FoodOrdering({ token, user, activeBooking }) {
  // ── View states: 'browse' | 'menu' | 'cart' | 'tracking'
  const [view, setView] = useState('browse');
  const [restaurants, setRestaurants] = useState([]);
  const [selectedRestaurant, setSelectedRestaurant] = useState(null);
  const [menuData, setMenuData] = useState(null);
  const [cart, setCart] = useState([]);
  const [businessTypeFilter, setBusinessTypeFilter] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [activeOrder, setActiveOrder] = useState(null);
  const [activeOrders, setActiveOrders] = useState([]); // all currently in-progress orders
  const [myOrders, setMyOrders] = useState([]);
  const [loadingRestaurants, setLoadingRestaurants] = useState(true);
  const [loadingMenu, setLoadingMenu] = useState(false);
  const [placingOrder, setPlacingOrder] = useState(false);
  const [trackingData, setTrackingData] = useState(null);
  const [deliveryAddress, setDeliveryAddress] = useState('');
  const [deliveryPostcode, setDeliveryPostcode] = useState('');
  const [notes, setNotes] = useState('');
  const [deliveryType, setDeliveryType] = useState('delivery');
  const [error, setError] = useState('');
  // Review state
  const [reviewRating, setReviewRating] = useState(0);
  const [reviewHover, setReviewHover] = useState(0);
  const [reviewSubmitting, setReviewSubmitting] = useState(false);
  const [reviewSubmitted, setReviewSubmitted] = useState(false);
  const [selectedPastOrder, setSelectedPastOrder] = useState(null);
  const [complaintText, setComplaintText] = useState('');
  const trackingPollRef = useRef(null);
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const riderMarkerRef = useRef(null);

  // Promo code state
  const [promoCode, setPromoCode] = useState('');
  const [promoDiscount, setPromoDiscount] = useState(0);
  const [promoValid, setPromoValid] = useState(null); // null | {valid, discount, description}
  const [promoLoading, setPromoLoading] = useState(false);

  // Tipping state
  const [tipAmount, setTipAmount] = useState(0);
  const [customTip, setCustomTip] = useState('');

  // Scheduled order state
  const [isScheduled, setIsScheduled] = useState(false);
  const [scheduledFor, setScheduledFor] = useState('');

  // Review comment
  const [reviewComment, setReviewComment] = useState('');

  // Report a Problem (replaces old refund flow)
  const [showProblemModal, setShowProblemModal] = useState(null); // order id
  const [problemOrder, setProblemOrder] = useState(null); // full order object
  const [problemStep, setProblemStep] = useState('select'); // select | details | evidence | result
  const [problemType, setProblemType] = useState('');
  const [problemDescription, setProblemDescription] = useState('');
  const [problemAffectedItems, setProblemAffectedItems] = useState([]); // [{menuItemId, name, quantity, price}]
  const [problemSubmitting, setProblemSubmitting] = useState(false);
  const [problemResult, setProblemResult] = useState(null);
  const [evidenceFiles, setEvidenceFiles] = useState([]); // File objects for upload
  const [walletBalance, setWalletBalance] = useState(null);

  // Legacy compat
  const [showRefundModal, setShowRefundModal] = useState(null);
  const [refundReason, setRefundReason] = useState('');
  const [refundIssueType, setRefundIssueType] = useState('other');

  // Delivery address mode: 'stay' | 'current' | 'manual'
  const [addressMode, setAddressMode] = useState(activeBooking?.propertyFullAddress ? 'stay' : 'manual');
  const [geoLocating, setGeoLocating] = useState(false);

  // Delivery fee quote (dynamic pricing)
  const [deliveryQuote, setDeliveryQuote] = useState(null);
  const [quoteLoading, setQuoteLoading] = useState(false);
  const [quoteExpirySeconds, setQuoteExpirySeconds] = useState(null);

  // Auto-fill delivery address from active property booking
  useEffect(() => {
    if (activeBooking && activeBooking.propertyFullAddress) {
      setDeliveryAddress(activeBooking.propertyAddress || activeBooking.propertyFullAddress.split(',')[0]?.trim() || '');
      setDeliveryPostcode(activeBooking.propertyPostcode || '');
    }
  }, [activeBooking]);

  useEffect(() => {
    if (!token) return; // wait for auth to be ready
    loadRestaurants();
    loadMyOrders();
  }, [token]);

  useEffect(() => {
    if (!token) return;
    loadRestaurants();
  }, [businessTypeFilter, token]);

  // Fetch delivery fee quote when entering cart
  useEffect(() => {
    if (view === 'cart' && deliveryType === 'delivery' && selectedRestaurant && !deliveryQuote) {
      fetchDeliveryQuote();
    }
  }, [view, deliveryType, selectedRestaurant]);

  // Quote expiry countdown
  useEffect(() => {
    if (!deliveryQuote?.expiresAt) { setQuoteExpirySeconds(null); return; }
    const tick = () => {
      const remaining = Math.max(0, Math.floor((new Date(deliveryQuote.expiresAt) - Date.now()) / 1000));
      setQuoteExpirySeconds(remaining);
      if (remaining <= 0) { setDeliveryQuote(null); setQuoteExpirySeconds(null); }
    };
    tick();
    const iv = setInterval(tick, 1000);
    return () => clearInterval(iv);
  }, [deliveryQuote?.expiresAt]);

  // Start/stop tracking poll when in tracking view
  useEffect(() => {
    if (view === 'tracking' && activeOrder) {
      pollTracking();
      trackingPollRef.current = setInterval(pollTracking, 8000);
    } else {
      if (trackingPollRef.current) clearInterval(trackingPollRef.current);
    }
    return () => {
      if (trackingPollRef.current) clearInterval(trackingPollRef.current);
    };
  }, [view, activeOrder]);

  // Init/cleanup tracking map
  useEffect(() => {
    let initTimer = null;
    let retryTimer = null;
    let unmounted = false;

    if (view === 'tracking') {
      // Clean up old map instance if exists
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove();
        mapInstanceRef.current = null;
        riderMarkerRef.current = null;
      }
      initTimer = setTimeout(() => {
        if (unmounted) return;
        initTrackingMapSafe(unmounted, (timer) => { retryTimer = timer; });
      }, 400);
    }
    return () => {
      unmounted = true;
      if (initTimer) clearTimeout(initTimer);
      if (retryTimer) clearInterval(retryTimer);
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove();
        mapInstanceRef.current = null;
        riderMarkerRef.current = null;
      }
    };
  }, [view, activeOrder]);

  // Update rider marker on map
  useEffect(() => {
    if (trackingData?.delivery?.riderLocation) {
      updateRiderOnMap(trackingData.delivery.riderLocation);
    }
  }, [trackingData]);

  const loadRestaurants = async () => {
    setLoadingRestaurants(true);
    try {
      const data = await restaurantAPI.browseRestaurantsFiltered(
        { businessType: businessTypeFilter },
        token
      );
      setRestaurants(data);
    } catch (e) {
      setError('Could not load restaurants');
    } finally {
      setLoadingRestaurants(false);
    }
  };

  const loadMyOrders = async () => {
    try {
      const data = await restaurantAPI.getMyOrders(token);
      setMyOrders(data);
      // Collect ALL active orders (not just the first one)
      const allActive = data.filter(o =>
        ['placed', 'accepted', 'preparing', 'ready_for_pickup', 'out_for_delivery'].includes(o.status)
      );
      setActiveOrders(allActive);
      // If no active order is currently being tracked, select the most recent
      if (!activeOrder && allActive.length > 0) {
        setActiveOrder(allActive[0]);
      }
    } catch (e) { console.error('Failed to load my orders:', e); }

    // Load wallet balance
    try {
      const w = await restaurantAPI.getWalletBalance(token);
      setWalletBalance(w);
    } catch (e) { /* wallet not critical */ }
  };

  const openMenu = async (restaurant) => {
    setLoadingMenu(true);
    setSelectedRestaurant(restaurant);
    setView('menu');
    try {
      const data = await restaurantAPI.getRestaurantMenu(restaurant.id || restaurant._id, token);
      setMenuData(data);
    } catch (e) {
      setError('Could not load menu');
    } finally {
      setLoadingMenu(false);
    }
  };

  // Cart management
  const addToCart = (item) => {
    setCart(prev => {
      const existing = prev.find(c => c.menuItemId === (item._id || item.id));
      if (existing) {
        return prev.map(c =>
          c.menuItemId === (item._id || item.id)
            ? { ...c, quantity: c.quantity + 1 }
            : c
        );
      }
      return [...prev, {
        menuItemId: item._id || item.id,
        name: item.name,
        price: item.price,
        quantity: 1,
        allergenNotes: null,
      }];
    });
  };

  const removeFromCart = (menuItemId) => {
    setCart(prev => {
      const existing = prev.find(c => c.menuItemId === menuItemId);
      if (existing && existing.quantity > 1) {
        return prev.map(c => c.menuItemId === menuItemId ? { ...c, quantity: c.quantity - 1 } : c);
      }
      return prev.filter(c => c.menuItemId !== menuItemId);
    });
  };

  const clearCart = () => setCart([]);

  const validatePromo = async () => {
    if (!promoCode.trim() || !selectedRestaurant) return;
    setPromoLoading(true);
    try {
      const subtotal = cart.reduce((sum, item) => sum + item.price * item.quantity, 0);
      const result = await restaurantAPI.validatePromoCode(
        promoCode, selectedRestaurant.id || selectedRestaurant._id, subtotal, token
      );
      setPromoValid(result);
      setPromoDiscount(result.discount);
    } catch (e) {
      setPromoValid({ valid: false, error: e.response?.data?.detail || 'Invalid promo code' });
      setPromoDiscount(0);
    } finally {
      setPromoLoading(false);
    }
  };

  const cartTotal = cart.reduce((sum, item) => sum + item.price * item.quantity, 0);
  const cartCount = cart.reduce((sum, item) => sum + item.quantity, 0);
  // Platform is NOT VAT-registered. Menu prices are gross (VAT-inclusive where applicable).
  // No VAT added at checkout — clean, honest pricing.
  const vatAmount = 0;
  const isCollectionOnly = selectedRestaurant?.fulfillmentMode === 'collection_only';
  const isSelfDelivery = selectedRestaurant?.fulfillmentMode === 'self_delivery';
  // Force collection when restaurant is collection_only
  const effectiveDeliveryType = isCollectionOnly ? 'collection' : deliveryType;
  const deliveryFee = effectiveDeliveryType === 'delivery'
    ? (isSelfDelivery ? (selectedRestaurant?.selfDeliveryFee || 0) : (deliveryQuote?.deliveryFee || 1.49))
    : 0;
  // Variable service fee: 5% of basket (min £0.99, max £2.49)
  const serviceFee = Math.round(Math.min(2.49, Math.max(0.99, cartTotal * 0.05)) * 100) / 100;
  // Small order surcharge: £1.00 on orders under £12
  const smallOrderFee = cartTotal < 12 ? 1.00 : 0;
  const grandTotal = cartTotal + vatAmount + deliveryFee + serviceFee + smallOrderFee - promoDiscount + tipAmount;

  // Fetch dynamic delivery fee quote when address coordinates are available
  const fetchDeliveryQuote = async () => {
    if (!selectedRestaurant || deliveryType !== 'delivery' || !token) return;
    // Use restaurant coordinates + a rough guess for delivery coords
    // In production this would use geocoding — for now we use restaurant coords + offset
    setQuoteLoading(true);
    try {
      const rLat = selectedRestaurant.latitude || selectedRestaurant.lat || 51.505;
      const rLng = selectedRestaurant.longitude || selectedRestaurant.lng || -0.09;
      // Offset by ~3km to simulate delivery distance (in production, geocode the address)
      const dLat = rLat + 0.027; // ~3km
      const dLng = rLng + 0.015;
      const quote = await deliveryAPI.getDeliveryQuote(
        selectedRestaurant._id || selectedRestaurant.id,
        dLat, dLng, token
      );
      setDeliveryQuote(quote);
    } catch (e) {
      console.error('Failed to get delivery quote:', e);
    } finally {
      setQuoteLoading(false);
    }
  };

  const placeOrder = async () => {
    if (effectiveDeliveryType === 'delivery') {
      if (!deliveryAddress.trim()) {
        setError('Please enter a delivery address');
        return;
      }
      if (!deliveryPostcode.trim()) {
        setError('Postcode is required for delivery');
        return;
      }
    }
    if (cart.length === 0) return;

    setPlacingOrder(true);
    setError('');
    try {
      const fullAddress = effectiveDeliveryType === 'delivery'
        ? `${deliveryAddress.trim()}, ${deliveryPostcode.trim()}`
        : 'Collection';
      const orderData = {
        restaurantId: selectedRestaurant.id || selectedRestaurant._id,
        items: cart,
        deliveryAddress: fullAddress,
        deliveryPostcode: deliveryPostcode.trim() || null,
        notes: notes || null,
        deliveryType: effectiveDeliveryType,
        promoCode: promoCode.trim() || null,
        tipAmount: tipAmount,
        scheduledFor: isScheduled && scheduledFor ? scheduledFor : null,
      };
      const order = await restaurantAPI.placeOrder(orderData, token);

      // Migrate from cash-on-delivery to card-in-app. After the order
      // record is created on the backend, kick the user into Stripe
      // Checkout to actually pay. The order's paymentStatus flips to
      // "paid" only after the webhook fires; until then it sits in
      // "pending" and the restaurant won't be notified.
      try {
        const { paymentsAPI } = await import('../services/api');
        const orderId = order._id || order.id;
        const { checkoutUrl } = await paymentsAPI.createFoodOrderCheckout(
          orderId,
          window.location.origin,
          token,
        );
        if (checkoutUrl) {
          window.location.href = checkoutUrl;
          return;
        }
      } catch (payErr) {
        // If checkout creation fails (Stripe down, network), keep the
        // order in pending state and surface a clear error. The user can
        // retry payment from their order list.
        console.error('Could not start checkout:', payErr);
        setError(
          payErr.response?.data?.detail
            || 'Order saved but we could not start payment. You can complete it from your orders.',
        );
      }

      setActiveOrder(order);
      setActiveOrders(prev => [order, ...prev]);
      clearCart();
      setNotes('');
      setPromoCode('');
      setPromoDiscount(0);
      setPromoValid(null);
      setTipAmount(0);
      setCustomTip('');
      setIsScheduled(false);
      setScheduledFor('');
      setView('tracking');
      await loadMyOrders();
    } catch (e) {
      console.error('Failed to place order:', e);
      setError(e.response?.data?.detail || 'Could not place order');
    } finally {
      setPlacingOrder(false);
    }
  };

  const pollTracking = async () => {
    if (!activeOrder?._id && !activeOrder?.id) return;
    try {
      const data = await restaurantAPI.trackOrder(activeOrder._id || activeOrder.id, token);
      setTrackingData(data);
      // If delivered, refresh
      if (data.orderStatus === 'delivered' || data.orderStatus === 'cancelled') {
        if (trackingPollRef.current) clearInterval(trackingPollRef.current);
        await loadMyOrders();
      }
    } catch (e) { console.error('Failed to poll tracking data:', e); }
  };

  // ── Map (safe init with unmount guard)
  const initTrackingMapSafe = (unmountedFlag, setRetryTimer) => {
    if (mapInstanceRef.current) return;
    const L = window.L;
    if (!L) return;

    const tryInit = () => {
      if (unmountedFlag) return true; // stop retrying
      const container = mapRef.current;
      if (!container || !document.contains(container) || container.offsetWidth === 0 || container.offsetHeight === 0) return false;
      try {
        const map = L.map(container).setView([51.505, -0.09], 14);
        L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
          attribution: '© OpenStreetMap'
        }).addTo(map);
        mapInstanceRef.current = map;
        setTimeout(() => {
          if (!unmountedFlag && mapInstanceRef.current) {
            try { map.invalidateSize(); } catch (e) {}
          }
        }, 200);

        // Add delivery destination pin
        const tracking = trackingData || {};
        const bounds = [];
        if (tracking.delivery?.deliveryLatitude && tracking.delivery?.deliveryLongitude) {
          const homeIcon = L.divIcon({
            html: `<div style="background:#3b82f6;color:#fff;border-radius:50%;width:32px;height:32px;display:flex;align-items:center;justify-content:center;font-size:16px;border:2px solid #fff;box-shadow:0 2px 6px rgba(0,0,0,0.4)">🏠</div>`,
            className: '', iconSize: [32, 32], iconAnchor: [16, 32]
          });
          L.marker([tracking.delivery.deliveryLatitude, tracking.delivery.deliveryLongitude], { icon: homeIcon })
            .addTo(map)
            .bindPopup('Your delivery address');
          bounds.push([tracking.delivery.deliveryLatitude, tracking.delivery.deliveryLongitude]);
        }

        // Restaurant pin
        if (tracking.delivery?.restaurantLatitude && tracking.delivery?.restaurantLongitude) {
          const restIcon = L.divIcon({
            html: `<div style="background:#f59e0b;color:#fff;border-radius:50%;width:32px;height:32px;display:flex;align-items:center;justify-content:center;font-size:16px;border:2px solid #fff;box-shadow:0 2px 6px rgba(0,0,0,0.4)">🍽</div>`,
            className: '', iconSize: [32, 32], iconAnchor: [16, 32]
          });
          L.marker([tracking.delivery.restaurantLatitude, tracking.delivery.restaurantLongitude], { icon: restIcon })
            .addTo(map)
            .bindPopup(selectedRestaurant?.restaurantName || 'Restaurant');
          bounds.push([tracking.delivery.restaurantLatitude, tracking.delivery.restaurantLongitude]);
        }

        if (bounds.length >= 2) {
          map.fitBounds(bounds, { padding: [40, 40] });
        }
        return true;
      } catch (e) {
        console.error('Map init error:', e);
        return false;
      }
    };

    // Retry until container is visible
    if (!tryInit()) {
      const timer = setInterval(() => {
        if (unmountedFlag || tryInit()) clearInterval(timer);
      }, 300);
      if (setRetryTimer) setRetryTimer(timer);
      setTimeout(() => clearInterval(timer), 10000);
    }
  };

  const updateRiderOnMap = (location) => {
    const L = window.L;
    if (!L || !mapInstanceRef.current || !location?.lat) return;

    const riderIcon = L.divIcon({
      html: `<div style="background:#16a34a;color:#fff;border-radius:50%;width:36px;height:36px;display:flex;align-items:center;justify-content:center;font-size:18px;border:3px solid #fff;box-shadow:0 2px 8px rgba(0,0,0,0.5)">🛵</div>`,
      className: '', iconSize: [36, 36], iconAnchor: [18, 18]
    });

    if (!riderMarkerRef.current) {
      riderMarkerRef.current = L.marker([location.lat, location.lng], { icon: riderIcon })
        .addTo(mapInstanceRef.current)
        .bindPopup(`${trackingData?.delivery?.riderName || 'Rider'} is on the way!`);
    } else {
      riderMarkerRef.current.setLatLng([location.lat, location.lng]);
    }
    mapInstanceRef.current.panTo([location.lat, location.lng]);
  };

  // ── Submit restaurant review
  const submitRestaurantReview = async () => {
    if (!reviewRating || !activeOrder) return;
    setReviewSubmitting(true);
    try {
      await restaurantAPI.rateOrder(activeOrder._id || activeOrder.id, reviewRating, reviewComment, token);
      setReviewSubmitted(true);
      // Update the order locally to reflect it's been rated
      setActiveOrder(prev => prev ? { ...prev, rated: true, rating: reviewRating } : prev);
    } catch (e) {
      console.error('Failed to submit review:', e);
      setError(e.response?.data?.detail || 'Could not submit review');
    } finally {
      setReviewSubmitting(false);
    }
  };

  const downloadReceipt = (order) => {
    const itemsHtml = (order.items || []).map(item => `
      <tr>
        <td style="padding:8px 0;border-bottom:1px solid #f0f0f0">${item.quantity}× ${item.name}</td>
        <td style="padding:8px 0;border-bottom:1px solid #f0f0f0;text-align:right;font-weight:600">£${(item.price * item.quantity).toFixed(2)}</td>
      </tr>
    `).join('');

    const html = `<!DOCTYPE html>
<html><head><meta charset="UTF-8"><title>Receipt ${order.orderNumber}</title>
<style>
  @media print { body { margin: 0; } .no-print { display: none; } }
  body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; max-width: 500px; margin: 40px auto; padding: 0 20px; color: #1a1a1a; }
  .header { text-align: center; border-bottom: 2px solid #1F3A68; padding-bottom: 20px; margin-bottom: 20px; }
  .logo { font-size: 24px; font-weight: 800; color: #1F3A68; margin-bottom: 4px; }
  .subtitle { font-size: 11px; color: #888; text-transform: uppercase; letter-spacing: 1px; }
  .order-info { background: #f8f9fa; border-radius: 8px; padding: 16px; margin-bottom: 20px; }
  .order-info p { margin: 4px 0; font-size: 13px; color: #555; }
  .order-info strong { color: #1a1a1a; }
  table { width: 100%; border-collapse: collapse; margin-bottom: 20px; }
  th { text-align: left; padding: 10px 0; border-bottom: 2px solid #e0e0e0; font-size: 12px; text-transform: uppercase; color: #888; letter-spacing: 0.5px; }
  th:last-child { text-align: right; }
  .totals td { padding: 6px 0; font-size: 14px; }
  .totals .grand-total td { padding-top: 12px; border-top: 2px solid #1F3A68; font-size: 18px; font-weight: 800; }
  .footer { text-align: center; margin-top: 30px; padding-top: 20px; border-top: 1px solid #e0e0e0; }
  .footer p { font-size: 11px; color: #999; margin: 2px 0; }
  .status-badge { display: inline-block; padding: 4px 12px; border-radius: 20px; font-size: 12px; font-weight: 600; }
  .status-delivered { background: #dcfce7; color: #166534; }
  .status-cancelled { background: #fee2e2; color: #991b1b; }
  .print-btn { display: block; margin: 20px auto; padding: 10px 30px; background: #1F3A68; color: white; border: none; border-radius: 8px; font-size: 14px; cursor: pointer; }
  .print-btn:hover { background: #2d5299; }
  .delivery-info { background: #f0f9ff; border-radius: 8px; padding: 12px 16px; margin-bottom: 20px; font-size: 13px; color: #1e40af; }
</style></head><body>
  <div class="header">
    <div class="logo">Ariv</div>
    <div class="subtitle">Order Receipt</div>
  </div>
  <div class="order-info">
    <p><strong>Order:</strong> #${order.orderNumber}</p>
    <p><strong>Restaurant:</strong> ${order.restaurantName || 'N/A'}</p>
    <p><strong>Date:</strong> ${new Date(order.createdAt).toLocaleDateString('en-GB', { weekday: 'long', day: '2-digit', month: 'long', year: 'numeric' })}</p>
    <p><strong>Time:</strong> ${new Date(order.createdAt).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}</p>
    <p><strong>Status:</strong> <span class="status-badge ${order.status === 'delivered' ? 'status-delivered' : 'status-cancelled'}">${(order.status || '').replace(/_/g, ' ')}</span></p>
  </div>
  <table>
    <thead><tr><th>Item</th><th>Amount</th></tr></thead>
    <tbody>${itemsHtml}</tbody>
  </table>
  <table class="totals">
    <tr><td>Subtotal</td><td style="text-align:right">£${(order.subtotal || 0).toFixed(2)}</td></tr>
    ${order.deliveryType === 'delivery' ? `<tr><td>Delivery Fee</td><td style="text-align:right">£${(order.deliveryFee || 0).toFixed(2)}</td></tr>` : ''}
    <tr><td>Service Fee (5%)</td><td style="text-align:right">£${(order.guestServiceFee || 0).toFixed(2)}</td></tr>
    ${order.smallOrderFee ? `<tr><td>Small Order Fee</td><td style="text-align:right">£${(order.smallOrderFee || 0).toFixed(2)}</td></tr>` : ''}
    <tr class="grand-total"><td>Total</td><td style="text-align:right">£${(order.total || 0).toFixed(2)}</td></tr>
  </table>
  ${order.deliveryAddress && order.deliveryType !== 'collection' ? `<div class="delivery-info">📍 Delivered to: ${order.deliveryAddress}</div>` : ''}
  ${order.deliveryType === 'collection' ? `<div class="delivery-info">🏃 Collection order</div>` : ''}
  ${order.notes ? `<div class="delivery-info">📝 ${order.notes}</div>` : ''}
  <button class="print-btn no-print" onclick="window.print()">🖨 Print / Save as PDF</button>
  <div class="footer">
    <p>Thank you for ordering with Ariv</p>
    <p>hello@ariv.co.uk</p>
  </div>
</body></html>`;

    const blob = new Blob([html], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `receipt-${order.orderNumber}.html`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const reorderToBasket = (order) => {
    // Set the restaurant from the order
    const rest = restaurants.find(r => (r.id || r._id) === order.restaurantId);
    if (rest) {
      setSelectedRestaurant(rest);
    } else {
      // Create a minimal restaurant object from order data
      setSelectedRestaurant({
        id: order.restaurantId,
        _id: order.restaurantId,
        restaurantName: order.restaurantName || 'Restaurant',
      });
    }
    // Populate cart with order items
    const newCart = (order.items || []).map(item => ({
      menuItemId: item.menuItemId || item._id || item.id || item.name,
      name: item.name,
      price: item.price,
      quantity: item.quantity,
      allergenNotes: null,
    }));
    setCart(newCart);
    // Pre-fill delivery address if available
    if (order.deliveryAddress && order.deliveryType === 'delivery') {
      const parts = order.deliveryAddress.split(',');
      if (parts.length > 1) {
        setDeliveryPostcode(parts[parts.length - 1].trim());
        setDeliveryAddress(parts.slice(0, -1).join(',').trim());
      } else {
        setDeliveryAddress(order.deliveryAddress);
      }
    }
    setDeliveryType(order.deliveryType || 'delivery');
    setSelectedPastOrder(null);
    setView('cart');
  };

  // ── Group menu items by category
  const groupedMenu = menuData?.menuItems?.reduce((acc, item) => {
    const cat = item.category || 'Other';
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(item);
    return acc;
  }, {}) || {};

  const filteredRestaurants = restaurants.filter(r => {
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    return (
      r.restaurantName?.toLowerCase().includes(q) ||
      r.cuisineTypes?.some(c => c.toLowerCase().includes(q)) ||
      r.city?.toLowerCase().includes(q)
    );
  }).sort((a, b) => {
    // Open restaurants first, closed/busy at the bottom
    if (a.isBusy && !b.isBusy) return 1;
    if (!a.isBusy && b.isBusy) return -1;
    return 0;
  });

  const orderStatusInfo = {
    placed: { label: 'Order Received', icon: '📋', color: 'text-blue-600', bg: 'bg-blue-50', step: 1 },
    accepted: { label: 'Confirmed', icon: '✅', color: 'text-green-600', bg: 'bg-green-50', step: 2 },
    preparing: { label: 'Being Prepared', icon: '👨‍🍳', color: 'text-yellow-600', bg: 'bg-yellow-50', step: 3 },
    ready_for_pickup: { label: 'Ready — Rider Assigned', icon: '📦', color: 'text-purple-600', bg: 'bg-purple-50', step: 4 },
    out_for_delivery: { label: 'On The Way!', icon: '🛵', color: 'text-orange-600', bg: 'bg-orange-50', step: 5 },
    delivered: { label: 'Delivered!', icon: '🎉', color: 'text-green-700', bg: 'bg-green-50', step: 6 },
    cancelled: { label: 'Cancelled', icon: '❌', color: 'text-red-600', bg: 'bg-red-50', step: 0 },
    rejected: { label: 'Rejected', icon: '❌', color: 'text-red-600', bg: 'bg-red-50', step: 0 },
  };

  // ─── BROWSE VIEW ─────────────────────────────────────────────────────────────
  if (view === 'browse') {
    return (
      <div>
        {/* Search & Filter bar */}
        <div className="mb-4">
          <input
            type="text"
            placeholder="Search restaurants, cuisine…"
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            className="w-full border border-gray-300 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 mb-3"
          />
          <div className="flex gap-2 overflow-x-auto pb-1">
            {BUSINESS_TYPES.map(bt => (
              <button
                key={bt.key}
                onClick={() => setBusinessTypeFilter(bt.key)}
                className={`flex-shrink-0 px-3 py-1.5 rounded-full text-sm font-medium border transition-all ${
                  businessTypeFilter === bt.key
                    ? 'bg-blue-600 text-white border-blue-600'
                    : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'
                }`}
              >
                {bt.icon} {bt.label}
              </button>
            ))}
          </div>
        </div>

        {/* Active orders banner — shows all in-progress orders */}
        {activeOrders.length > 0 && (
          <div className="mb-4 space-y-2">
            {activeOrders.map(order => (
          <div
                key={order._id || order.id}
            className="bg-green-50 border border-green-200 rounded-xl p-4 flex items-center gap-3 cursor-pointer hover:bg-green-100 transition-colors"
                onClick={() => { setActiveOrder(order); setView('tracking'); }}
          >
            <div className="text-2xl animate-bounce">
                  {orderStatusInfo[order.status]?.icon || '📦'}
            </div>
            <div className="flex-1">
                  <p className="font-semibold text-green-900 text-sm">Active order from {order.restaurantName}</p>
                  <p className="text-green-700 text-xs">{orderStatusInfo[order.status]?.label}</p>
            </div>
            <span className="text-green-600 text-sm font-medium">Track →</span>
          </div>
            ))}
          </div>
        )}

        {error && (
          <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-3 text-red-700 text-sm">{error}</div>
        )}

        {loadingRestaurants ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {[1,2,3,4].map(i => (
              <div key={i} className="bg-gray-100 rounded-xl h-40 animate-pulse" />
            ))}
          </div>
        ) : filteredRestaurants.length === 0 ? (
          <div className="text-center py-12 text-gray-400">
            <div className="text-5xl mb-3">🍽</div>
            <p>No restaurants found</p>
            <p className="text-sm mt-1">Try a different category or search term</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {filteredRestaurants.map(r => {
              const isComingSoon = r.availabilityStatus === 'coming_soon';
              return (
              <div
                key={r.id || r._id}
                className={`rounded-xl border overflow-hidden transition-shadow ${
                  isComingSoon
                    ? 'bg-amber-50/50 border-amber-200 cursor-not-allowed opacity-90'
                    : r.isBusy
                      ? 'bg-gray-50 border-gray-300 opacity-60 hover:opacity-80 cursor-pointer'
                      : 'bg-white border-gray-200 hover:shadow-lg cursor-pointer'
                }`}
                onClick={() => { if (!isComingSoon) openMenu(r); }}
                title={isComingSoon ? 'Coming soon — Ariv is still rolling out delivery in this area.' : undefined}
              >
                {/* Cover image / placeholder */}
                <div className={`h-32 flex items-center justify-center relative ${
                  isComingSoon
                    ? 'bg-gradient-to-br from-amber-100 to-amber-50'
                    : r.isBusy
                      ? 'bg-gradient-to-br from-gray-200 to-gray-100'
                      : 'bg-gradient-to-br from-orange-100 to-yellow-50'
                }`}>
                  {r.coverImageUrl ? (
                    <img src={resolveImg(r.coverImageUrl)} alt={r.restaurantName} className={`w-full h-full object-cover ${(isComingSoon || r.isBusy) ? 'grayscale' : ''}`} />
                  ) : (
                    <span className={`text-5xl ${(isComingSoon || r.isBusy) ? 'grayscale opacity-50' : ''}`}>
                      {BUSINESS_TYPES.find(bt => bt.key === r.businessType)?.icon || '🍽'}
                    </span>
                  )}
                  {r.fhrsRating !== null && r.fhrsRating !== undefined && (
                    <div className="absolute top-2 right-2 bg-white rounded-full px-2 py-0.5 text-xs font-bold text-green-700 border border-green-200">
                      FHRS {r.fhrsRating}★
                    </div>
                  )}
                  {isComingSoon && (
                    <div className="absolute top-2 left-2 bg-amber-500 text-white rounded-full px-3 py-1 text-xs font-bold shadow-lg flex items-center gap-1">
                      <span className="w-2 h-2 rounded-full bg-white inline-block" />
                      Coming soon
                    </div>
                  )}
                  {!isComingSoon && r.isBusy && (
                    <div className="absolute top-2 left-2 bg-red-600 text-white rounded-full px-3 py-1 text-xs font-bold shadow-lg flex items-center gap-1">
                      <span className="w-2 h-2 rounded-full bg-white/60 inline-block" />
                      {r.busyUntil ? `Closed · Opens ~${new Date(r.busyUntil).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}` : 'Closed'}
                    </div>
                  )}
                  {!isComingSoon && !r.isBusy && r.defaultPrepTime && (
                    <div className="absolute bottom-2 left-2 bg-white/90 rounded-full px-2 py-0.5 text-xs font-semibold text-gray-700 border border-gray-200">
                      ~{r.defaultPrepTime} min prep
                    </div>
                  )}
                </div>

                <div className="p-4">
                  <div className="flex items-start justify-between">
                    <div>
                      <h3 className="font-bold text-gray-900">{r.restaurantName}</h3>
                      <p className="text-xs text-gray-500 mt-0.5">
                        {r.cuisineTypes?.join(', ') || r.businessType}
                      </p>
                    </div>
                    {r.rating > 0 && (
                      <div className="flex items-center gap-1 text-sm">
                        <span className="text-yellow-500">⭐</span>
                        <span className="font-semibold text-gray-700">{r.rating?.toFixed(1)}</span>
                        <span className="text-gray-400 text-xs">({r.totalRatings})</span>
                      </div>
                    )}
                  </div>

                  <div className="flex items-center gap-3 mt-3 text-xs text-gray-500">
                    <span>⏱ {r.estimatedDeliveryTime || '30-45 mins'}</span>
                    <span>·</span>
                    <span>Min £{r.minimumOrderValue?.toFixed(2) || '10.00'}</span>
                    <span>·</span>
                    <span>{r.city}</span>
                  </div>
                  <div className="mt-2">
                    <span className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full font-medium ${
                      r.fulfillmentMode === 'collection_only'
                        ? 'bg-amber-50 text-amber-600 border border-amber-200'
                        : r.fulfillmentMode === 'self_delivery'
                          ? 'bg-green-50 text-green-600 border border-green-200'
                          : 'bg-blue-50 text-blue-600 border border-blue-200'
                    }`}>
                      {r.fulfillmentMode === 'collection_only' ? '🛍️ Collection Only' : r.fulfillmentMode === 'self_delivery' ? '🏪 Own Delivery' : '🛵 Platform Delivery'}
                    </span>
                  </div>
                  {isComingSoon && (
                    <p className="text-xs text-amber-700 mt-2">
                      Ariv is still building our rider network in this area. Check back soon.
                    </p>
                  )}
                </div>
              </div>
              );
            })}
          </div>
        )}

        {/* App Wallet Balance */}
        {walletBalance && walletBalance.balance > 0 && (
          <div className="mt-6 bg-gradient-to-r from-emerald-50 to-green-50 border border-emerald-200 rounded-xl p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-semibold text-emerald-600 uppercase">App Wallet</p>
                <p className="text-2xl font-bold text-emerald-800">£{walletBalance.balance.toFixed(2)}</p>
                <p className="text-xs text-emerald-600 mt-0.5">Auto-applied to your next order</p>
              </div>
              <span className="text-3xl">👛</span>
            </div>
          </div>
        )}

        {/* Past orders */}
        {myOrders.filter(o => ['delivered', 'cancelled'].includes(o.status)).length > 0 && (
          <div className="mt-8">
            <h3 className="font-semibold text-gray-800 mb-3">Past Orders</h3>
            {myOrders.filter(o => ['delivered', 'cancelled'].includes(o.status)).slice(0, 5).map(o => (
              <div
                key={o._id}
                onClick={() => setSelectedPastOrder(o)}
                className="bg-white rounded-xl border border-gray-200 p-4 mb-3 flex items-center justify-between cursor-pointer hover:border-blue-300 hover:shadow-sm transition-all"
              >
                <div>
                  <p className="font-semibold text-gray-900 text-sm">{o.restaurantName}</p>
                  <p className="text-xs text-gray-500">
                    {new Date(o.createdAt).toLocaleDateString('en-GB')} · {o.items?.length} item{o.items?.length !== 1 ? 's' : ''}
                    {o.rated && <span className="ml-1 text-yellow-500">{'⭐'.repeat(o.rating || 0)}</span>}
                    {o.status === 'cancelled' && <span className="ml-1 text-red-500 font-medium">Cancelled</span>}
                  </p>
                </div>
                <div className="text-right flex items-center gap-3">
                  <p className="font-bold text-gray-900">£{o.total?.toFixed(2)}</p>
                  <span className="text-gray-400 text-sm">›</span>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* ── Report a Problem Modal (multi-step Just Eat-style flow) ── */}
        {showProblemModal && problemOrder && (() => {
          const PROBLEM_TYPES = [
            { key: 'missing_items', icon: '📦', label: 'Missing items', desc: 'Items were not in the delivery', needsItems: true },
            { key: 'wrong_items', icon: '🔄', label: 'Wrong item(s)', desc: 'Received different items', needsItems: true },
            { key: 'order_not_delivered', icon: '🚫', label: 'Order never arrived', desc: 'Marked delivered but never received', needsItems: false },
            { key: 'spilt_or_damaged', icon: '💧', label: 'Spilt or damaged', desc: 'Food arrived damaged or spilt', needsItems: true },
            { key: 'poor_quality', icon: '👎', label: 'Poor food quality', desc: 'Food was cold, stale, or inedible', needsItems: false },
            { key: 'late_delivery', icon: '⏰', label: 'Very late delivery', desc: 'Arrived much later than estimated', needsItems: false },
            { key: 'allergen_issue', icon: '⚠️', label: 'Allergen / dietary issue', desc: "Didn't match allergen info", needsItems: false },
            { key: 'other', icon: '💬', label: 'Something else', desc: 'Any other issue', needsItems: false },
          ];
          const selectedType = PROBLEM_TYPES.find(t => t.key === problemType);
          const toggleAffectedItem = (item) => {
            const id = item.menuItemId || item._id || item.id;
            const exists = problemAffectedItems.find(a => (a.menuItemId || a._id) === id);
            if (exists) {
              setProblemAffectedItems(prev => prev.filter(a => (a.menuItemId || a._id) !== id));
            } else {
              setProblemAffectedItems(prev => [...prev, { menuItemId: id, name: item.name, quantity: item.quantity || 1, price: item.price || 0 }]);
            }
          };
          return (
            <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-[60]" onClick={() => { setShowProblemModal(null); setProblemOrder(null); }}>
              <div className="bg-white rounded-2xl shadow-xl max-w-md w-full mx-4 max-h-[85vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
                {/* Header */}
                <div className="sticky top-0 bg-white border-b border-gray-100 px-5 py-4 rounded-t-2xl flex items-center justify-between">
                  <div>
                    <h3 className="font-bold text-lg text-gray-900">Report a Problem</h3>
                    <p className="text-xs text-gray-500">Order #{problemOrder.orderNumber}</p>
                  </div>
                  <button onClick={() => { setShowProblemModal(null); setProblemOrder(null); }} className="p-1.5 hover:bg-gray-100 rounded-full">
                    <span className="text-lg">✕</span>
                  </button>
                </div>
                <div className="px-5 py-4">
                  {/* Step 1: Select problem type */}
                  {problemStep === 'select' && (
                    <div>
                      <p className="text-sm font-medium text-gray-700 mb-3">What went wrong?</p>
                      <div className="space-y-2">
                        {PROBLEM_TYPES.map(type => (
                          <button key={type.key} onClick={() => { setProblemType(type.key); setProblemAffectedItems([]); setProblemStep('details'); }}
                            className="w-full flex items-center gap-3 p-3 border border-gray-200 rounded-xl hover:border-amber-300 hover:bg-amber-50 transition-colors text-left">
                            <span className="text-xl">{type.icon}</span>
                            <div>
                              <p className="text-sm font-medium text-gray-900">{type.label}</p>
                              <p className="text-xs text-gray-500">{type.desc}</p>
                            </div>
                            <span className="ml-auto text-gray-400">›</span>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                  {/* Step 2: Details + affected items */}
                  {problemStep === 'details' && selectedType && (
                    <div>
                      <button onClick={() => setProblemStep('select')} className="text-sm text-blue-600 hover:underline mb-3 flex items-center gap-1">← Back</button>
                      <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 mb-4">
                        <p className="text-sm font-medium text-amber-800">{selectedType.icon} {selectedType.label}</p>
                      </div>
                      {selectedType.needsItems && problemOrder.items?.length > 0 && (
                        <div className="mb-4">
                          <p className="text-sm font-medium text-gray-700 mb-2">Which items were affected?</p>
                          <div className="space-y-1.5">
                            {problemOrder.items.map((item, i) => {
                              const id = item.menuItemId || item._id || item.id || i;
                              const isSelected = problemAffectedItems.some(a => (a.menuItemId || a._id) === id);
                              return (
                                <button key={id} onClick={() => toggleAffectedItem({ ...item, menuItemId: id })}
                                  className={`w-full flex items-center gap-3 p-2.5 border rounded-lg text-left transition-colors ${isSelected ? 'border-amber-400 bg-amber-50' : 'border-gray-200 hover:bg-gray-50'}`}>
                                  <div className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 ${isSelected ? 'bg-amber-500 border-amber-500' : 'border-gray-300'}`}>
                                    {isSelected && <span className="text-white text-xs">✓</span>}
                                  </div>
                                  <span className="text-sm text-gray-800 flex-1">{item.quantity}× {item.name}</span>
                                  <span className="text-sm text-gray-500">£{(item.price * item.quantity).toFixed(2)}</span>
                                </button>
                              );
                            })}
                          </div>
                          {problemAffectedItems.length === 0 && (
                            <p className="text-xs text-amber-600 mt-2">Please select at least one affected item</p>
                          )}
                        </div>
                      )}
                      <div className="mb-4">
                        <p className="text-sm font-medium text-gray-700 mb-2">Tell us more (optional)</p>
                        <textarea value={problemDescription} onChange={e => setProblemDescription(e.target.value)}
                          placeholder="Add any details that might help us resolve this..." rows={3}
                          className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-amber-500" />
                      </div>
                      {/* Photo evidence upload */}
                      <div className="mb-4">
                        <p className="text-sm font-medium text-gray-700 mb-2">Upload photo evidence (optional)</p>
                        <p className="text-xs text-gray-500 mb-2">Photo of receipt, bag contents, or damaged items helps speed up your claim.</p>
                        <input type="file" accept="image/*" multiple
                          onChange={e => setEvidenceFiles(Array.from(e.target.files || []))}
                          className="text-sm text-gray-600 file:mr-3 file:py-1.5 file:px-3 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-amber-50 file:text-amber-700 hover:file:bg-amber-100" />
                        {evidenceFiles.length > 0 && (
                          <p className="text-xs text-green-600 mt-1">{evidenceFiles.length} photo(s) selected</p>
                        )}
                      </div>
                      <button disabled={problemSubmitting || (selectedType.needsItems && problemAffectedItems.length === 0)}
                        onClick={async () => {
                          setProblemSubmitting(true);
                          try {
                            // Upload evidence photos first
                            for (const file of evidenceFiles) {
                              try { await restaurantAPI.uploadEvidence(showProblemModal, file, token); } catch (e) { console.error('Evidence upload failed:', e); }
                            }
                            const result = await restaurantAPI.reportProblem(showProblemModal, { issueType: problemType, description: problemDescription, affectedItems: problemAffectedItems }, token);
                            setProblemResult(result);
                            setProblemStep('result');
                            setEvidenceFiles([]);
                            loadMyOrders();
                          } catch (e) { alert(e.response?.data?.detail || 'Failed to submit report'); }
                          finally { setProblemSubmitting(false); }
                        }}
                        className="w-full py-3 bg-amber-600 text-white rounded-xl text-sm font-bold hover:bg-amber-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
                        {problemSubmitting ? 'Submitting...' : 'Submit Report'}
                      </button>
                    </div>
                  )}
                  {/* Step 3: Result */}
                  {problemStep === 'result' && problemResult && (
                    <div className="text-center py-4">
                      {problemResult.refundEligible ? (
                        <>
                          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4"><span className="text-3xl">{problemResult.walletCredited ? '👛' : '💷'}</span></div>
                          <h4 className="font-bold text-gray-900 text-lg mb-2">{problemResult.walletCredited ? 'Refund Credited to Wallet' : 'Refund Submitted'}</h4>
                          <p className="text-sm text-gray-600 mb-3">{problemResult.message}</p>
                          {problemResult.refundAmount > 0 && (
                            <div className={`border rounded-xl p-3 mb-4 ${problemResult.walletCredited ? 'bg-emerald-50 border-emerald-200' : 'bg-green-50 border-green-200'}`}>
                              <p className="text-sm font-medium text-green-800">Refund amount: £{problemResult.refundAmount.toFixed(2)}</p>
                              {problemResult.walletCredited ? (
                                <p className="text-xs text-emerald-600 mt-1">Credited to your App Wallet — automatically applied to your next order</p>
                              ) : (
                                <p className="text-xs text-green-600 mt-1">Food items only — delivery fee and service fees are non-refundable. The restaurant has 24 hours to respond.</p>
                              )}
                            </div>
                          )}
                        </>
                      ) : (
                        <>
                          <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4"><span className="text-3xl">📋</span></div>
                          <h4 className="font-bold text-gray-900 text-lg mb-2">Report Received</h4>
                          <p className="text-sm text-gray-600 mb-3">{problemResult.message}</p>
                        </>
                      )}
                      <button onClick={() => { setShowProblemModal(null); setProblemOrder(null); setSelectedPastOrder(null); }}
                        className="w-full py-3 bg-gray-900 text-white rounded-xl text-sm font-bold hover:bg-gray-800 transition-colors">
                        Done
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })()}

        {/* Past Order Detail Modal */}
        {selectedPastOrder && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setSelectedPastOrder(null)}>
            <div className="bg-white rounded-2xl w-full max-w-md max-h-[90vh] overflow-y-auto shadow-2xl" onClick={e => e.stopPropagation()}>
              {/* Header */}
              <div className="sticky top-0 bg-white border-b border-gray-100 px-5 py-4 rounded-t-2xl flex items-center justify-between">
                <div>
                  <h3 className="font-bold text-gray-900">Order #{selectedPastOrder.orderNumber}</h3>
                  <p className="text-xs text-gray-500">{selectedPastOrder.restaurantName}</p>
                </div>
                <button onClick={() => setSelectedPastOrder(null)} className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center hover:bg-gray-200 text-gray-500">✕</button>
              </div>

              {/* Status */}
              <div className="px-5 py-3">
                <div className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold ${
                  selectedPastOrder.status === 'delivered' ? 'bg-green-100 text-green-700'
                  : selectedPastOrder.status === 'cancelled' ? 'bg-red-100 text-red-700'
                  : 'bg-gray-100 text-gray-700'
                }`}>
                  {selectedPastOrder.status === 'delivered' ? '✅' : '❌'} {selectedPastOrder.status?.replace(/_/g, ' ')}
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  {new Date(selectedPastOrder.createdAt).toLocaleDateString('en-GB', { weekday: 'long', day: '2-digit', month: 'long', year: 'numeric' })} at {new Date(selectedPastOrder.createdAt).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}
                </p>
              </div>

              {/* Items */}
              <div className="px-5 py-3 border-t border-gray-50">
                <p className="font-semibold text-gray-800 text-sm mb-2">Items</p>
                <div className="space-y-1.5">
                  {selectedPastOrder.items?.map((item, i) => (
                    <div key={i} className="flex justify-between text-sm">
                      <span className="text-gray-700">{item.quantity}× {item.name}</span>
                      <span className="font-medium text-gray-900">£{(item.price * item.quantity).toFixed(2)}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Price Breakdown */}
              <div className="px-5 py-3 border-t border-gray-100">
                <p className="font-semibold text-gray-800 text-sm mb-2">Price Breakdown</p>
                <div className="space-y-1.5 text-sm">
                  <div className="flex justify-between text-gray-600">
                    <span>Subtotal</span><span>£{(selectedPastOrder.subtotal || 0).toFixed(2)}</span>
                  </div>
                  {selectedPastOrder.deliveryType === 'delivery' && (
                    <div className="flex justify-between text-gray-600">
                      <span>Delivery Fee</span><span>£{(selectedPastOrder.deliveryFee || 0).toFixed(2)}</span>
                    </div>
                  )}
                  <div className="flex justify-between text-gray-600">
                    <span>Service Fee (5%)</span><span>£{(selectedPastOrder.guestServiceFee || 0).toFixed(2)}</span>
                  </div>
                  {selectedPastOrder.smallOrderFee > 0 && (
                    <div className="flex justify-between text-gray-500">
                      <span className="text-xs">Small Order Fee</span><span>£{(selectedPastOrder.smallOrderFee || 0).toFixed(2)}</span>
                    </div>
                  )}
                  <div className="flex justify-between font-bold text-gray-900 pt-2 border-t border-gray-200">
                    <span>Total</span><span>£{(selectedPastOrder.total || 0).toFixed(2)}</span>
                  </div>
                </div>
              </div>

              {/* Delivery Info */}
              {selectedPastOrder.deliveryAddress && selectedPastOrder.deliveryType !== 'collection' && (
                <div className="px-5 py-3 border-t border-gray-50">
                  <p className="text-xs text-gray-500">📍 {selectedPastOrder.deliveryAddress}</p>
                </div>
              )}
              {selectedPastOrder.deliveryType === 'collection' && (
                <div className="px-5 py-3 border-t border-gray-50">
                  <p className="text-xs text-gray-500">🏃 Collection order</p>
                </div>
              )}
              {selectedPastOrder.notes && (
                <div className="px-5 py-2">
                  <p className="text-xs text-gray-500">📝 {selectedPastOrder.notes}</p>
                </div>
              )}

              {/* Rating */}
              {selectedPastOrder.rated && (
                <div className="px-5 py-3 border-t border-gray-50">
                  <p className="text-sm text-gray-600">Your rating: {'⭐'.repeat(selectedPastOrder.rating || 0)}</p>
                </div>
              )}

              {/* Action buttons */}
              <div className="px-5 py-4 border-t border-gray-100 space-y-2">
                <button
                  onClick={() => downloadReceipt(selectedPastOrder)}
                  className="w-full py-2.5 text-center text-sm font-medium bg-gray-100 text-gray-800 rounded-xl hover:bg-gray-200 transition-colors flex items-center justify-center gap-2"
                >
                  🧾 Download Receipt
                </button>
                {selectedPastOrder.status === 'delivered' && (
                  <button
                    onClick={() => reorderToBasket(selectedPastOrder)}
                    className="w-full py-2.5 text-center text-sm font-medium bg-green-500 text-white rounded-xl hover:bg-green-600 transition-colors flex items-center justify-center gap-2"
                  >
                    🔄 Reorder to Basket
                  </button>
                )}
              {['delivered', 'cancelled', 'rejected'].includes(selectedPastOrder.status) && !selectedPastOrder.problemReported && !selectedPastOrder.refundStatus && (
                <button onClick={() => {
                  setProblemOrder(selectedPastOrder);
                  setShowProblemModal(selectedPastOrder._id || selectedPastOrder.id);
                  setProblemStep('select');
                  setProblemType('');
                  setProblemDescription('');
                  setProblemAffectedItems([]);
                  setProblemResult(null);
                }}
                  className="w-full py-2.5 text-center text-sm font-medium border border-amber-200 text-amber-700 rounded-xl hover:bg-amber-50 transition-colors flex items-center justify-center gap-2">
                  ⚠️ Report a Problem
                </button>
              )}
              {(selectedPastOrder.problemReported || selectedPastOrder.refundStatus) && (
                <div className="space-y-2">
                  {selectedPastOrder.problemReported && (
                    <div className="bg-amber-50 border border-amber-200 rounded-xl p-3">
                      <p className="text-xs font-medium text-amber-700">Problem reported: {selectedPastOrder.problemCategory || selectedPastOrder.problemType}</p>
                      {selectedPastOrder.problemResolutionMessage && (
                        <p className="text-xs text-amber-600 mt-1">{selectedPastOrder.problemResolutionMessage}</p>
                      )}
                    </div>
                  )}
                  {selectedPastOrder.refundStatus && (
                    <div className="flex justify-center">
                      <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                        selectedPastOrder.refundStatus === 'approved' ? 'bg-green-100 text-green-700' :
                        selectedPastOrder.refundStatus === 'requested' ? 'bg-yellow-100 text-yellow-700' : 'bg-gray-100 text-gray-600'
                      }`}>
                        Refund: {selectedPastOrder.refundStatus}
                        {selectedPastOrder.refundAmount > 0 && ` · £${selectedPastOrder.refundAmount.toFixed(2)}`}
                      </span>
                    </div>
                  )}
                </div>
              )}
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // ─── MENU VIEW ───────────────────────────────────────────────────────────────
  if (view === 'menu') {
    return (
      <div>
        {/* Restaurant header */}
        <div className="flex items-center gap-3 mb-5">
          <button
            onClick={() => { setView('browse'); setSelectedRestaurant(null); setMenuData(null); }}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
          >
            ← Back
          </button>
          <div className="flex-1">
            <h2 className="font-bold text-gray-900 text-lg">{selectedRestaurant?.restaurantName}</h2>
            <p className="text-xs text-gray-500">
              {selectedRestaurant?.estimatedDeliveryTime} · {selectedRestaurant?.city}
              {selectedRestaurant?.fhrsRating !== null && selectedRestaurant?.fhrsRating !== undefined && (
                <span className="ml-2 bg-green-100 text-green-700 px-1.5 py-0.5 rounded-full">FHRS {selectedRestaurant.fhrsRating}★</span>
              )}
            </p>
          </div>
          {cartCount > 0 && (
            <button
              onClick={() => setView('cart')}
              className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-full text-sm font-semibold"
            >
              🛒 {cartCount} · £{cartTotal.toFixed(2)}
            </button>
          )}
        </div>

        {menuData?.isBusy && (
          <div className="bg-red-50 border border-red-300 rounded-xl p-3 mb-4 flex items-center gap-2">
            <span className="text-lg">🔴</span>
            <p className="text-red-800 text-sm font-medium">
              {menuData.busyUntil
                ? `This restaurant is currently closed — expected to reopen around ${new Date(menuData.busyUntil).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}. You can browse the menu but cannot order yet.`
                : 'This restaurant is currently closed. You can browse the menu but cannot place orders right now.'}
            </p>
          </div>
        )}

        {!menuData?.isBusy && menuData?.defaultPrepTime && (
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-3 mb-4 flex items-center gap-2">
            <span className="text-lg">🕐</span>
            <p className="text-blue-700 text-sm font-medium">Estimated prep time: ~{menuData.defaultPrepTime} minutes</p>
          </div>
        )}

        {loadingMenu ? (
          <div className="space-y-3">
            {[1,2,3,4].map(i => <div key={i} className="h-20 bg-gray-100 rounded-xl animate-pulse" />)}
          </div>
        ) : (
          <div>
            {Object.entries(groupedMenu).map(([category, items]) => (
              <div key={category} className="mb-6">
                <h3 className="text-sm font-bold text-gray-700 uppercase tracking-wide mb-3 px-1">
                  {category}
                </h3>
                <div className="space-y-2">
                  {items.map(item => {
                    const inCart = cart.find(c => c.menuItemId === (item._id || item.id));
                    return (
                      <div key={item._id || item.id} className="bg-white rounded-xl border border-gray-200 p-4 flex gap-4 hover:shadow-sm transition-shadow">
                        {/* Item image placeholder */}
                        <div className="w-16 h-16 rounded-lg bg-gradient-to-br from-orange-100 to-yellow-50 flex-shrink-0 flex items-center justify-center overflow-hidden">
                          {item.imageUrl ? (
                            <img src={resolveImg(item.imageUrl)} alt={item.name} className="w-full h-full object-cover rounded-lg" />
                          ) : (
                            <span className="text-2xl">🍽</span>
                          )}
                        </div>

                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between gap-2">
                            <div>
                              <h4 className="font-semibold text-gray-900 text-sm">{item.name}</h4>
                              <p className="text-xs text-gray-500 mt-0.5 line-clamp-2">{item.description}</p>
                            </div>
                            <span className="font-bold text-gray-900 text-sm whitespace-nowrap">£{item.price?.toFixed(2)}</span>
                          </div>

                          {/* Badges */}
                          <div className="flex flex-wrap gap-1.5 mt-2">
                            {item.isVegan && <span className="text-xs bg-green-100 text-green-700 px-1.5 py-0.5 rounded">🌿 Vegan</span>}
                            {item.isVegetarian && !item.isVegan && <span className="text-xs bg-green-50 text-green-600 px-1.5 py-0.5 rounded">🥦 Veggie</span>}
                            {item.isGlutenFree && <span className="text-xs bg-yellow-100 text-yellow-700 px-1.5 py-0.5 rounded">GF</span>}
                            {item.isHalal && <span className="text-xs bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded">☪ Halal</span>}
                            {item.spiceLevel > 0 && <span className="text-xs">{SPICE[item.spiceLevel]}</span>}
                            {item.calories && <span className="text-xs text-gray-400">{item.calories} kcal</span>}
                            {item.prepTime && <span className="text-xs text-gray-400">⏱ {item.prepTime}m</span>}
                          </div>

                          {/* Allergens */}
                          {item.allergens?.length > 0 && (
                            <p className="text-xs text-amber-600 mt-1">
                              Contains: {item.allergens.join(', ')}
                            </p>
                          )}

                          {/* Add to cart */}
                          <div className="mt-3">
                            {!inCart ? (
                              <button
                                onClick={() => addToCart(item)}
                                disabled={!item.isAvailable}
                                className={`px-4 py-1.5 rounded-lg text-sm font-semibold transition-colors ${
                                  item.isAvailable
                                    ? 'bg-blue-600 hover:bg-blue-700 text-white'
                                    : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                                }`}
                              >
                                {item.isAvailable ? 'Add' : 'Unavailable'}
                              </button>
                            ) : (
                              <div className="flex items-center gap-3">
                                <button onClick={() => removeFromCart(item._id || item.id)} className="w-7 h-7 bg-gray-200 rounded-full font-bold text-gray-700 hover:bg-gray-300 flex items-center justify-center">−</button>
                                <span className="font-bold text-gray-900 text-sm">{inCart.quantity}</span>
                                <button onClick={() => addToCart(item)} className="w-7 h-7 bg-blue-600 rounded-full font-bold text-white hover:bg-blue-700 flex items-center justify-center">+</button>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}

            {/* Allergen notice */}
            <div className="mt-4 p-4 bg-amber-50 border border-amber-200 rounded-xl">
              <p className="text-xs text-amber-700">
                <strong>⚠ Allergen Information:</strong> If you have any food allergies or dietary requirements, please inform the restaurant in your order notes. For a full list of allergens, contact the restaurant directly.
              </p>
            </div>
          </div>
        )}

        {/* Floating cart button */}
        {cartCount > 0 && (
          <div className="fixed bottom-6 left-0 right-0 flex justify-center px-4 z-50">
            <button
              onClick={() => setView('cart')}
              className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3.5 rounded-2xl font-bold shadow-2xl flex items-center gap-3 transition-all"
            >
              <span className="bg-white text-blue-600 rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold">{cartCount}</span>
              View Basket · £{cartTotal.toFixed(2)}
            </button>
          </div>
        )}
      </div>
    );
  }

  // ─── CART VIEW ───────────────────────────────────────────────────────────────
  if (view === 'cart') {
    return (
      <div>
        <div className="flex items-center gap-3 mb-5">
          <button onClick={() => setView('menu')} className="p-2 hover:bg-gray-100 rounded-full">← Back</button>
          <h2 className="font-bold text-gray-900 text-lg">Your Basket</h2>
        </div>

        {error && (
          <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-3 text-red-700 text-sm">{error}</div>
        )}

        {/* Cart items */}
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden mb-4">
          <div className="px-4 py-3 border-b border-gray-100">
            <span className="font-semibold text-gray-800">{selectedRestaurant?.restaurantName}</span>
          </div>
          {cart.map(item => (
            <div key={item.menuItemId} className="px-4 py-3 flex items-center gap-3 border-b border-gray-50 last:border-b-0">
              <div className="flex items-center gap-2">
                <button onClick={() => removeFromCart(item.menuItemId)} className="w-6 h-6 bg-gray-200 rounded-full text-sm font-bold hover:bg-gray-300 flex items-center justify-center">−</button>
                <span className="font-bold text-gray-900 w-5 text-center">{item.quantity}</span>
                <button onClick={() => addToCart({ _id: item.menuItemId, ...item })} className="w-6 h-6 bg-blue-600 rounded-full text-sm font-bold text-white hover:bg-blue-700 flex items-center justify-center">+</button>
              </div>
              <span className="flex-1 text-sm text-gray-800">{item.name}</span>
              <span className="font-semibold text-gray-900 text-sm">£{(item.price * item.quantity).toFixed(2)}</span>
            </div>
          ))}
        </div>

        {/* Delivery type */}
        <div className="bg-white rounded-xl border border-gray-200 p-4 mb-4">
          <p className="font-semibold text-gray-800 mb-3">Order Type</p>
          {selectedRestaurant?.fulfillmentMode === 'collection_only' ? (
            <div className="py-2.5 rounded-lg text-sm font-medium bg-amber-50 border border-amber-200 text-amber-800 text-center">
              🛍️ Collection Only — this restaurant does not offer delivery today
            </div>
          ) : (
            <div className="flex gap-3">
              {['delivery', 'collection'].map(type => (
                <button
                  key={type}
                  onClick={() => setDeliveryType(type)}
                  className={`flex-1 py-2.5 rounded-lg text-sm font-medium border transition-all capitalize ${
                    deliveryType === type
                      ? 'bg-blue-600 text-white border-blue-600'
                      : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'
                  }`}
                >
                  {type === 'delivery' ? '🛵 Delivery' : '🏃 Collection'}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Delivery address + postcode */}
        {deliveryType === 'delivery' && (
          <div className="bg-white rounded-xl border border-gray-200 p-4 mb-4">
            <p className="font-semibold text-gray-800 mb-3">Delivery Address</p>

            {/* Address mode selector */}
            <div className="flex gap-2 mb-3">
              {activeBooking?.propertyFullAddress && (
                <button
                  onClick={() => {
                    setAddressMode('stay');
                    setDeliveryAddress(activeBooking.propertyAddress || activeBooking.propertyFullAddress.split(',')[0]?.trim() || '');
                    setDeliveryPostcode(activeBooking.propertyPostcode || '');
                  }}
                  className={`flex-1 py-2 rounded-lg text-xs font-medium border transition-all ${
                    addressMode === 'stay'
                      ? 'bg-blue-600 text-white border-blue-600'
                      : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'
                  }`}
                >
                  🏠 Stay Address
                </button>
              )}
              <button
                onClick={() => {
                  setAddressMode('current');
                  setGeoLocating(true);
                  navigator.geolocation.getCurrentPosition(
                    async (pos) => {
                      try {
                        const res = await fetch(`https://nominatim.openstreetmap.org/reverse?lat=${pos.coords.latitude}&lon=${pos.coords.longitude}&format=json&addressdetails=1`);
                        const data = await res.json();
                        const addr = data.address || {};
                        const road = [addr.house_number, addr.road].filter(Boolean).join(' ');
                        const area = addr.suburb || addr.city_district || addr.town || addr.city || '';
                        setDeliveryAddress([road, area].filter(Boolean).join(', ') || data.display_name?.split(',').slice(0, 3).join(',') || '');
                        setDeliveryPostcode((addr.postcode || '').toUpperCase());
                      } catch {
                        setDeliveryAddress(`${pos.coords.latitude.toFixed(5)}, ${pos.coords.longitude.toFixed(5)}`);
                      }
                      setGeoLocating(false);
                    },
                    () => {
                      setGeoLocating(false);
                      setAddressMode('manual');
                      alert('Could not get your location. Please enter your address manually.');
                    },
                    { enableHighAccuracy: true, timeout: 10000 }
                  );
                }}
                className={`flex-1 py-2 rounded-lg text-xs font-medium border transition-all ${
                  addressMode === 'current'
                    ? 'bg-blue-600 text-white border-blue-600'
                    : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'
                }`}
              >
                📍 Current Location
              </button>
              <button
                onClick={() => {
                  setAddressMode('manual');
                  setDeliveryAddress('');
                  setDeliveryPostcode('');
                }}
                className={`flex-1 py-2 rounded-lg text-xs font-medium border transition-all ${
                  addressMode === 'manual'
                    ? 'bg-blue-600 text-white border-blue-600'
                    : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'
                }`}
              >
                ✏️ Enter Manually
              </button>
            </div>

            {/* Stay address — read-only display */}
            {addressMode === 'stay' && activeBooking?.propertyFullAddress && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-3">
                <p className="text-xs font-medium text-blue-700 mb-1">🏠 Delivering to your stay at {activeBooking.propertyTitle || 'your property'}</p>
                <p className="text-sm font-semibold text-blue-900">{activeBooking.propertyFullAddress}</p>
              </div>
            )}

            {/* Current location — loading or result */}
            {addressMode === 'current' && geoLocating && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-3 text-center">
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600 mx-auto mb-2" />
                <p className="text-sm text-blue-700">Getting your location...</p>
              </div>
            )}

            {/* Editable fields for 'current' (once resolved) and 'manual' */}
            {(addressMode === 'manual' || (addressMode === 'current' && !geoLocating)) && (
              <>
                <input
                  type="text"
                  placeholder="Street address, flat number, building..."
                  value={deliveryAddress}
                  onChange={e => setDeliveryAddress(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 mb-3"
                />
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1 block">
                    Postcode <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    placeholder="e.g. SW1A 1AA"
                    value={deliveryPostcode}
                    onChange={e => setDeliveryPostcode(e.target.value.toUpperCase())}
                    className={`w-full border rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      !deliveryPostcode.trim() && deliveryAddress.trim() ? 'border-red-300 bg-red-50' : 'border-gray-300'
                    }`}
                    maxLength={8}
                  />
                  {!deliveryPostcode.trim() && deliveryAddress.trim() && (
                    <p className="text-xs text-red-500 mt-1">Postcode is required for accurate delivery</p>
                  )}
                </div>
              </>
            )}
          </div>
        )}

        {/* Collection — show restaurant address */}
        {deliveryType === 'collection' && selectedRestaurant && (
          <div className="bg-white rounded-xl border border-gray-200 p-4 mb-4">
            <p className="font-semibold text-gray-800 mb-2">Collection Address</p>
            <div className="bg-blue-50 rounded-lg p-3">
              <p className="font-medium text-blue-900 text-sm">📍 {selectedRestaurant.restaurantName}</p>
              <p className="text-blue-700 text-sm mt-1">
                {selectedRestaurant.address || selectedRestaurant.fullAddress || 'Address not provided'}
                {selectedRestaurant.postcode && `, ${selectedRestaurant.postcode}`}
              </p>
              {selectedRestaurant.city && (
                <p className="text-blue-600 text-xs mt-0.5">{selectedRestaurant.city}</p>
              )}
              <p className="text-xs text-blue-500 mt-2">
                🕐 Estimated ready time: {selectedRestaurant.deliveryTimeMin || 15}–{selectedRestaurant.deliveryTimeMax || 30} mins
              </p>
            </div>
          </div>
        )}

        {/* Notes */}
        <div className="bg-white rounded-xl border border-gray-200 p-4 mb-4">
          <p className="font-semibold text-gray-800 mb-2">Special Instructions</p>
          <textarea
            placeholder="Allergies, dietary requirements, or any special requests…"
            value={notes}
            onChange={e => setNotes(e.target.value)}
            rows={3}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
          />
          <p className="text-xs text-amber-600 mt-1">⚠ Always declare allergens here and contact the restaurant directly for severe allergies.</p>
        </div>

        {/* Price breakdown */}
        <div className="bg-white rounded-xl border border-gray-200 p-4 mb-6">
          <p className="font-semibold text-gray-800 mb-3">Price Breakdown</p>
          {/* Delivery model badge */}
          {deliveryType === 'delivery' && (
            <div className={`mb-3 px-3 py-1.5 rounded-lg text-xs font-medium inline-flex items-center gap-1.5 ${
              isSelfDelivery
                ? 'bg-green-50 text-green-700 border border-green-200'
                : 'bg-blue-50 text-blue-700 border border-blue-200'
            }`}>
              {isSelfDelivery ? '🏪 Delivered by Restaurant' : '🛵 Delivered by Ariv'}
            </div>
          )}
          <div className="space-y-2 text-sm">
            <div className="flex justify-between text-gray-600">
              <span>Subtotal</span><span>£{cartTotal.toFixed(2)}</span>
            </div>
            {deliveryType === 'delivery' && (
              <div>
                <div className="flex justify-between text-gray-600">
                  <span>Delivery fee{isSelfDelivery ? '' : (quoteLoading ? ' (calculating…)' : '')}</span>
                  <span>{isSelfDelivery && deliveryFee === 0 ? 'Free' : `${deliveryQuote?.currencySymbol || '£'}${deliveryFee.toFixed(2)}`}</span>
                </div>
                {deliveryQuote && (
                  <>
                    <div className="text-xs text-gray-400 mt-0.5 flex gap-3 flex-wrap">
                      <span>{deliveryQuote.distanceKm?.toFixed(1)} km</span>
                      <span>~{deliveryQuote.estimatedMinutes} min</span>
                      {deliveryQuote.breakdown?.tierLabel && (
                        <span className="text-blue-500 font-medium">{deliveryQuote.breakdown.tierLabel} distance</span>
                      )}
                      {deliveryQuote.surgeActive && (
                        <span className="text-orange-500 font-medium">⚡ Busy area +£{deliveryQuote.surgeFlat?.toFixed(2)}</span>
                      )}
                      {deliveryQuote.isPriority && (
                        <span className="text-violet-600 font-medium">🚀 Priority</span>
                      )}
                    </div>
                    <div className="text-xs mt-0.5 flex gap-3 items-center">
                      <span className="text-gray-300">{deliveryQuote.routingEngine === 'HAVERSINE_ESTIMATE' ? 'Estimated route' : deliveryQuote.routingEngine}</span>
                      {quoteExpirySeconds != null && quoteExpirySeconds > 0 && (
                        <span className={`font-medium ${quoteExpirySeconds < 60 ? 'text-red-500' : 'text-gray-400'}`}>
                          Quote expires: {Math.floor(quoteExpirySeconds / 60)}:{String(quoteExpirySeconds % 60).padStart(2, '0')}
                        </span>
                      )}
                    </div>
                  </>
                )}
              </div>
            )}
            <div className="flex justify-between text-gray-600">
              <span>Service fee (5%)</span><span>£{serviceFee.toFixed(2)}</span>
            </div>
            {smallOrderFee > 0 && (
              <div className="flex justify-between text-gray-500">
                <span className="text-xs">Small order fee</span><span>£{smallOrderFee.toFixed(2)}</span>
              </div>
            )}
              {promoDiscount > 0 && (
                <div className="flex justify-between text-green-600">
                  <span>Promo Discount</span>
                  <span>-£{promoDiscount.toFixed(2)}</span>
                </div>
              )}
              {tipAmount > 0 && (
                <div className="flex justify-between text-gray-600">
                  <span>Rider Tip</span>
                  <span>£{tipAmount.toFixed(2)}</span>
                </div>
              )}
            <div className="flex justify-between font-bold text-gray-900 text-base pt-2 border-t border-gray-200">
              <span>Total</span><span>£{grandTotal.toFixed(2)}</span>
            </div>
          </div>
          <p className="text-xs text-gray-400 mt-2">Cash on delivery / collection</p>
          <p className="text-xs text-gray-400 mt-1">Prices set by restaurant. VAT included in menu prices where applicable.</p>
        </div>

            {/* Promo Code */}
            <div className="bg-white rounded-xl border border-gray-200 p-4 mb-3">
              <p className="font-semibold text-gray-800 text-sm mb-2">🎟 Promo Code</p>
              <div className="flex gap-2">
                <input type="text" value={promoCode} onChange={e => { setPromoCode(e.target.value.toUpperCase()); setPromoValid(null); setPromoDiscount(0); }}
                  placeholder="Enter code" className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm uppercase focus:outline-none focus:ring-2 focus:ring-blue-400" />
                <button onClick={validatePromo} disabled={promoLoading || !promoCode.trim()}
                  className="px-4 py-2 bg-[#1F3A68] text-white rounded-lg text-sm font-medium hover:bg-[#2d5299] disabled:opacity-50">
                  {promoLoading ? '…' : 'Apply'}
                </button>
              </div>
              {promoValid && promoValid.valid && (
                <p className="text-green-600 text-xs mt-2 font-medium">✅ {promoValid.description} — Saving £{promoDiscount.toFixed(2)}</p>
              )}
              {promoValid && !promoValid.valid && (
                <p className="text-red-500 text-xs mt-2">❌ {promoValid.error}</p>
              )}
            </div>

            {/* Tip */}
            {deliveryType === 'delivery' && (
              <div className="bg-white rounded-xl border border-gray-200 p-4 mb-3">
                <p className="font-semibold text-gray-800 text-sm mb-2">💝 Add a tip for your rider</p>
                <div className="flex gap-2 mb-2">
                  {[0, 1, 2, 3, 5].map(t => (
                    <button key={t} onClick={() => { setTipAmount(t); setCustomTip(''); }}
                      className={`flex-1 py-2 rounded-lg text-sm font-medium border transition-colors ${
                        tipAmount === t && !customTip ? 'border-green-500 bg-green-50 text-green-700' : 'border-gray-200 text-gray-600 hover:bg-gray-50'
                      }`}>
                      {t === 0 ? 'No tip' : `£${t}`}
                    </button>
                  ))}
                </div>
                <input type="number" step="0.50" min="0" placeholder="Custom tip (£)" value={customTip}
                  onChange={e => { setCustomTip(e.target.value); setTipAmount(parseFloat(e.target.value) || 0); }}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-400" />
              </div>
            )}

            {/* Schedule Order */}
            <div className="bg-white rounded-xl border border-gray-200 p-4 mb-3">
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={isScheduled} onChange={e => setIsScheduled(e.target.checked)} className="rounded" />
                <span className="text-sm font-medium text-gray-700">📅 Schedule for later</span>
              </label>
              {isScheduled && (
                <input type="datetime-local" value={scheduledFor} onChange={e => setScheduledFor(e.target.value)}
                  min={new Date(Date.now() + 30 * 60000).toISOString().slice(0, 16)}
                  className="mt-2 w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400" />
              )}
            </div>

        {menuData?.isBusy && (
          <div className="bg-red-50 border border-red-300 rounded-xl p-3 mb-3 text-center">
            <p className="text-red-800 text-sm font-medium">🔴 This restaurant is currently closed and not accepting orders</p>
          </div>
        )}
        <button
          onClick={placeOrder}
          disabled={placingOrder || cart.length === 0 || menuData?.isBusy}
          className="w-full py-4 bg-green-500 hover:bg-green-600 text-white rounded-xl font-bold text-lg disabled:opacity-50 transition-colors shadow-lg shadow-green-200"
        >
          {menuData?.isBusy ? '🔴 Restaurant Closed — Cannot Order' : placingOrder ? 'Placing Order…' : `Place Order · £${grandTotal.toFixed(2)}`}
        </button>
      </div>
    );
  }

  // ─── TRACKING VIEW ───────────────────────────────────────────────────────────
  if (view === 'tracking') {
    const orderStatus = trackingData?.orderStatus || activeOrder?.status || 'placed';
    const info = orderStatusInfo[orderStatus] || orderStatusInfo.placed;
    const steps = ['placed', 'accepted', 'preparing', 'ready_for_pickup', 'out_for_delivery', 'delivered'];
    const currentStep = info.step || 0;

    return (
      <div>
        <div className="flex items-center gap-3 mb-5">
          <button onClick={() => setView('browse')} className="p-2 hover:bg-gray-100 rounded-full">← Browse</button>
          <h2 className="font-bold text-gray-900 text-lg">Track Your Order</h2>
        </div>

        {/* Status card */}
        <div className={`${info.bg} rounded-xl p-5 mb-5 text-center`}>
          <div className="text-4xl mb-2">{info.icon}</div>
          <h3 className={`text-xl font-bold ${info.color}`}>{info.label}</h3>
          {activeOrder?.restaurantName && (
            <p className="text-gray-600 text-sm mt-1">from {activeOrder.restaurantName}</p>
          )}
          {/* Delivery provider badge */}
          {deliveryType === 'delivery' && (
            <div className={`mt-2 inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium ${
              trackingData?.fulfillmentMode === 'self_delivery' || activeOrder?.fulfillmentMode === 'self_delivery'
                ? 'bg-green-100 text-green-700'
                : 'bg-blue-100 text-blue-700'
            }`}>
              {trackingData?.fulfillmentMode === 'self_delivery' || activeOrder?.fulfillmentMode === 'self_delivery'
                ? '🏪 Delivered by Restaurant'
                : '🛵 Delivered by Ariv'}
            </div>
          )}
          {trackingData?.delivery?.riderName && orderStatus === 'out_for_delivery' && (
            <div className="mt-1">
              <p className="text-gray-600 text-sm">🛵 {trackingData.delivery.riderName} is on the way</p>
              {/* Rider phone — only shown when rider is 80%+ of the way (to avoid distracting them) */}
              {trackingData.delivery.riderPhone && (
                <a href={`tel:${trackingData.delivery.riderPhone}`} className="inline-flex items-center gap-1 mt-1 bg-green-50 border border-green-200 rounded-full px-3 py-1 text-xs font-medium text-green-700 hover:bg-green-100">
                  📞 Call Rider: {trackingData.delivery.riderPhone}
                </a>
              )}
              {trackingData.delivery.deliveryProgress !== undefined && (
                <div className="mt-2 w-full bg-gray-200 rounded-full h-1.5">
                  <div className="bg-blue-600 h-1.5 rounded-full transition-all" style={{ width: `${Math.round(trackingData.delivery.deliveryProgress * 100)}%` }} />
                </div>
              )}
            </div>
          )}
          {/* Delivery PIN — shown from order confirmation onwards */}
          {(trackingData?.deliveryPin || activeOrder?.deliveryPin) && !['delivered', 'cancelled', 'rejected'].includes(orderStatus) && (
            <div className="mt-3 bg-blue-50 border border-blue-200 rounded-xl p-4">
              <p className="text-xs text-blue-600 font-semibold uppercase tracking-wide">Your Delivery PIN</p>
              <p className="text-3xl font-bold text-blue-800 tracking-[10px] text-center mt-2">
                {trackingData?.deliveryPin || activeOrder?.deliveryPin}
              </p>
              <p className="text-xs text-blue-500 mt-2">Give this PIN to your rider when they arrive to confirm delivery</p>
            </div>
          )}
        </div>

        {/* Progress tracker */}
        {orderStatus !== 'cancelled' && orderStatus !== 'rejected' && (
          <div className="bg-white rounded-xl border border-gray-200 p-4 mb-5">
            <div className="flex items-center justify-between">
              {steps.map((step, i) => {
                const stepInfo = orderStatusInfo[step];
                const done = currentStep > i + 1;
                const active = currentStep === i + 1;
                return (
                  <React.Fragment key={step}>
                    <div className="flex flex-col items-center gap-1">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm border-2 ${
                        done ? 'bg-green-500 border-green-500 text-white'
                        : active ? 'bg-blue-600 border-blue-600 text-white'
                        : 'bg-white border-gray-300 text-gray-400'
                      }`}>
                        {done ? '✓' : stepInfo?.icon || i + 1}
                      </div>
                    </div>
                    {i < steps.length - 1 && (
                      <div className={`flex-1 h-0.5 mx-1 ${done ? 'bg-green-500' : 'bg-gray-200'}`} />
                    )}
                  </React.Fragment>
                );
              })}
            </div>
          </div>
        )}

        {/* Delivery Map — shows throughout order lifecycle */}
        {!['delivered', 'cancelled', 'rejected'].includes(orderStatus) && (
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden mb-5">
            <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
              <span className="font-semibold text-gray-800">🗺 {['ready_for_pickup', 'out_for_delivery'].includes(orderStatus) ? 'Live Tracking' : 'Delivery Route'}</span>
              {trackingData?.delivery?.riderName && (
                <span className="text-xs text-green-600 bg-green-50 px-2 py-0.5 rounded-full">🛵 {trackingData.delivery.riderName}</span>
              )}
            </div>
            <div ref={mapRef} style={{ height: '280px' }} />
            {/* View in Google Maps link */}
            {trackingData?.delivery && (trackingData.delivery.restaurantLatitude || trackingData.delivery.deliveryLatitude) && (
              <div className="px-4 py-2 border-t border-gray-100 bg-gray-50">
                <a
                  href={`https://www.google.com/maps/dir/?api=1&origin=${trackingData.delivery.restaurantLatitude || ''},${trackingData.delivery.restaurantLongitude || ''}&destination=${trackingData.delivery.deliveryLatitude || ''},${trackingData.delivery.deliveryLongitude || ''}&travelmode=driving`}
                  target="_blank" rel="noopener noreferrer"
                  className="w-full flex items-center justify-center gap-2 text-sm font-medium text-green-700 hover:text-green-800 py-1"
                >
                  🧭 View Route in Google Maps
                </a>
              </div>
            )}
          </div>
        )}

        {/* Message Restaurant or Message Rider — context-dependent */}
        {!['delivered', 'cancelled', 'rejected'].includes(orderStatus) && (
          <div className="mb-5">
            {/* Show "Message Rider" when rider has picked up (out_for_delivery) */}
            {orderStatus === 'out_for_delivery' && trackingData?.riderUserId ? (
              <button
                onClick={() => {
                  const riderUserId = trackingData.riderUserId;
                  const assignmentId = trackingData.assignmentId;
                  if (!riderUserId) {
                    alert('Rider info not available yet. Please try again in a moment.');
                    return;
                  }
                  // Create or navigate to delivery_dropoff conversation with rider
                  import('../services/api').then(({ messagesAPI }) => {
                    messagesAPI.createConversation({
                      participantId: riderUserId,
                      conversationType: 'delivery_dropoff',
                      relatedType: 'delivery_assignment',
                      relatedId: assignmentId,
                      initialMessage: `Hi, regarding my order #${activeOrder?.orderNumber} — I have delivery instructions.`,
                    }, token).then((conv) => {
                      window.dispatchEvent(new CustomEvent('switchToServiceMessages', {
                        detail: { conversationId: conv._id, conversation: conv }
                      }));
                    }).catch(err => {
                      console.error('Failed to start conversation with rider:', err);
                      alert('Could not start conversation. Please try again.');
                    });
                  });
                }}
                className="w-full py-3 text-center text-sm font-medium bg-orange-600 text-white rounded-xl hover:bg-orange-700 transition-colors flex items-center justify-center gap-2"
              >
                🛵 Message Rider
              </button>
            ) : (
              <button
                onClick={() => {
                  const restaurantUserId = activeOrder?.restaurantUserId || activeOrder?.restaurant?.userId;
                  if (!restaurantUserId) {
                    alert('Unable to message restaurant — please try again in a moment.');
                    return;
                  }
                  // Create or navigate to conversation with restaurant
                  import('../services/api').then(({ messagesAPI }) => {
                    messagesAPI.createConversation({
                      participantId: restaurantUserId,
                      conversationType: 'restaurant',
                      relatedType: 'food_order',
                      relatedId: activeOrder._id || activeOrder.id,
                      initialMessage: `Hi, regarding my order #${activeOrder.orderNumber}`,
                    }, token).then((conv) => {
                      window.dispatchEvent(new CustomEvent('switchToServiceMessages', {
                        detail: { conversationId: conv._id, conversation: conv }
                      }));
                    }).catch(err => {
                      console.error('Failed to start conversation:', err);
                      alert('Could not start conversation. Please try again.');
                    });
                  });
                }}
                className="w-full py-3 text-center text-sm font-medium bg-[#1F3A68] text-white rounded-xl hover:bg-[#2d5299] transition-colors flex items-center justify-center gap-2"
              >
                💬 Message Restaurant
              </button>
            )}
          </div>
        )}

        {/* Order details */}
        {activeOrder && (
          <div className="bg-white rounded-xl border border-gray-200 p-4 mb-5">
            <p className="font-semibold text-gray-800 mb-3">Order #{activeOrder.orderNumber}</p>
            <div className="space-y-1 text-sm">
              {activeOrder.items?.map((item, i) => (
                <div key={i} className="flex justify-between text-gray-600">
                  <span>{item.quantity}× {item.name}</span>
                  <span>£{(item.price * item.quantity).toFixed(2)}</span>
                </div>
              ))}
              <div className="flex justify-between font-bold text-gray-900 pt-2 border-t border-gray-100">
                <span>Total</span>
                <span>£{activeOrder.total?.toFixed(2)}</span>
              </div>
            </div>
            {activeOrder.deliveryAddress && (
              <p className="text-xs text-gray-500 mt-3">📍 {activeOrder.deliveryAddress}</p>
            )}
          </div>
        )}

        {/* Other active orders — quick switch */}
        {activeOrders.filter(o => (o._id || o.id) !== (activeOrder?._id || activeOrder?.id)).length > 0 && (
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-3 mb-5">
            <p className="text-xs text-blue-700 font-semibold mb-2">Other Active Orders</p>
            <div className="space-y-1">
              {activeOrders.filter(o => (o._id || o.id) !== (activeOrder?._id || activeOrder?.id)).map(order => (
                <button
                  key={order._id || order.id}
                  onClick={() => setActiveOrder(order)}
                  className="w-full flex items-center gap-2 p-2 rounded-lg hover:bg-blue-100 transition-colors text-left text-sm"
                >
                  <span>{orderStatusInfo[order.status]?.icon || '📦'}</span>
                  <span className="flex-1 truncate">{order.restaurantName} — #{order.orderNumber}</span>
                  <span className="text-xs text-blue-600">{orderStatusInfo[order.status]?.label}</span>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Order more button */}
        {!['delivered', 'cancelled', 'rejected'].includes(orderStatus) && (
          <button
            onClick={() => setView('browse')}
            className="w-full py-3 text-center text-sm text-blue-600 font-medium hover:bg-blue-50 rounded-xl border border-blue-200 mb-5"
          >
            🍽 Browse & Order More
          </button>
        )}

        {/* Delivered — review & reorder */}
        {orderStatus === 'delivered' && (
          <div className="space-y-4">
            {/* Review section */}
            {!activeOrder?.rated && !reviewSubmitted ? (
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-5">
                <h4 className="font-bold text-amber-900 text-center mb-1">⭐ How was your order?</h4>
                <p className="text-amber-700 text-xs text-center mb-4">Rate {activeOrder?.restaurantName || 'the restaurant'}</p>

                {/* Star rating */}
                <div className="flex justify-center gap-2 mb-4">
                  {[1, 2, 3, 4, 5].map(star => (
                    <button
                      key={star}
                      onClick={() => setReviewRating(star)}
                      onMouseEnter={() => setReviewHover(star)}
                      onMouseLeave={() => setReviewHover(0)}
                      className="text-3xl transition-transform hover:scale-110 focus:outline-none"
                    >
                      {star <= (reviewHover || reviewRating) ? '⭐' : '☆'}
                    </button>
                  ))}
                </div>

                {reviewRating > 0 && (
                  <p className="text-center text-sm text-amber-800 mb-3">
                    {reviewRating === 1 ? 'Poor' : reviewRating === 2 ? 'Fair' : reviewRating === 3 ? 'Good' : reviewRating === 4 ? 'Very Good' : 'Excellent!'}
                  </p>
                )}

                <textarea value={reviewComment} onChange={e => setReviewComment(e.target.value)}
                  placeholder="Tell us about your experience (optional)…" rows={2}
                  className="w-full border border-gray-300 rounded-xl px-3 py-2 text-sm mt-3 focus:outline-none focus:ring-2 focus:ring-yellow-400 resize-none" />

                <button
                  onClick={submitRestaurantReview}
                  disabled={!reviewRating || reviewSubmitting}
                  className="w-full py-2.5 bg-amber-500 hover:bg-amber-600 text-white rounded-lg font-semibold text-sm disabled:opacity-50 transition-colors mt-3"
                >
                  {reviewSubmitting ? 'Submitting…' : `Submit ${reviewRating ? reviewRating + '-Star' : ''} Review`}
                </button>
              </div>
            ) : (
              <div className="bg-green-50 border border-green-200 rounded-xl p-4 text-center">
                <p className="text-2xl mb-1">🎉</p>
                <p className="font-bold text-green-800">Thank you for your review!</p>
                <p className="text-green-600 text-sm mt-1">
                  You rated {activeOrder?.restaurantName} {activeOrder?.rating || reviewRating} star{(activeOrder?.rating || reviewRating) !== 1 ? 's' : ''}
                </p>
              </div>
            )}

            {/* Reorder button */}
            <div className="bg-green-50 border border-green-200 rounded-xl p-4 text-center">
              <p className="font-bold text-green-800 mb-3">🍽 Enjoy your food!</p>
              <button
                onClick={() => {
                  setView('browse');
                  setActiveOrder(null);
                  setReviewRating(0);
                  setReviewHover(0);
                  setReviewSubmitted(false);
                }}
                className="px-6 py-2 bg-green-600 text-white rounded-lg font-semibold text-sm"
              >
                Order Again
              </button>
            </div>

            {/* Report Issue / Complaint */}
            <div className="bg-red-50 border border-red-200 rounded-xl p-4">
              <button
                onClick={() => {
                  const restaurantUserId = activeOrder?.restaurantUserId || activeOrder?.restaurant?.userId;
                  if (!restaurantUserId) {
                    alert('Unable to contact restaurant — please try again.');
                    return;
                  }
                  import('../services/api').then(({ messagesAPI }) => {
                    messagesAPI.createConversation({
                      participantId: restaurantUserId,
                      conversationType: 'restaurant',
                      relatedType: 'food_order',
                      relatedId: activeOrder._id || activeOrder.id,
                      initialMessage: `I'd like to report an issue with my order #${activeOrder.orderNumber}`,
                    }, token).then((conv) => {
                      window.dispatchEvent(new CustomEvent('switchToServiceMessages', {
                        detail: { conversationId: conv._id, conversation: conv }
                      }));
                    }).catch(err => {
                      console.error('Failed to start conversation:', err);
                      alert('Could not start conversation. Please try again.');
                    });
                  });
                }}
                className="w-full py-2.5 text-center text-sm font-medium text-red-700 hover:bg-red-100 rounded-lg transition-colors flex items-center justify-center gap-2"
              >
                ⚠️ Report an Issue with this Order
              </button>
              <p className="text-xs text-red-400 text-center mt-1">Contact the restaurant about any problems with your order</p>
            </div>
          </div>
        )}
      </div>
    );
  }

  // (Report a Problem modal is now rendered inline within the browse view above)

  return null;
}
